import React, { useEffect, useState } from 'react';
import { PlusIcon, ArrowRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { opportuniteService, clientService } from '../../api/services';
import {
  Pipeline,
  Opportunite,
  EtapeOpportunite,
  ETAPE_OPPORTUNITE_LABELS,
  Client,
} from '../../types';
import { MoneyDisplay } from '../../components/common/MoneyDisplay';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { Modal } from '../../components/common/Modal';
import toast from 'react-hot-toast';

/** Étape suivante dans le flux (pour le bouton « avancer »). */
const NEXT: Partial<Record<EtapeOpportunite, EtapeOpportunite>> = {
  [EtapeOpportunite.PROSPECTION]: EtapeOpportunite.QUALIFICATION,
  [EtapeOpportunite.QUALIFICATION]: EtapeOpportunite.PROPOSITION,
  [EtapeOpportunite.PROPOSITION]: EtapeOpportunite.NEGOCIATION,
  [EtapeOpportunite.NEGOCIATION]: EtapeOpportunite.GAGNEE,
};

const COLUMN_STYLE: Record<EtapeOpportunite, string> = {
  [EtapeOpportunite.PROSPECTION]: 'border-t-gray-400',
  [EtapeOpportunite.QUALIFICATION]: 'border-t-blue-400',
  [EtapeOpportunite.PROPOSITION]: 'border-t-indigo-500',
  [EtapeOpportunite.NEGOCIATION]: 'border-t-amber-500',
  [EtapeOpportunite.GAGNEE]: 'border-t-green-500',
  [EtapeOpportunite.PERDUE]: 'border-t-red-500',
};

export function CrmPipeline() {
  const [pipeline, setPipeline] = useState<Pipeline | null>(null);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState<Client[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<Partial<Opportunite>>({
    titre: '', montantEstime: 0, etape: EtapeOpportunite.PROSPECTION,
  });

  const load = async () => {
    try {
      setLoading(true);
      setPipeline(await opportuniteService.getPipeline());
    } catch {
      toast.error('Impossible de charger le pipeline');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    clientService.getAll({ size: 500 }).then((p) => setClients(p.content)).catch(() => {});
  }, []);

  const advance = async (o: Opportunite) => {
    const next = NEXT[o.etape];
    if (!next) return;
    await opportuniteService.changeEtape(o.id, next);
    toast.success(`→ ${ETAPE_OPPORTUNITE_LABELS[next]}`);
    load();
  };

  const lose = async (o: Opportunite) => {
    const motif = window.prompt('Motif de la perte ?');
    if (!motif) return;
    await opportuniteService.changeEtape(o.id, EtapeOpportunite.PERDUE, motif);
    toast.success('Opportunité marquée perdue');
    load();
  };

  const submitCreate = async () => {
    if (!form.titre) { toast.error('Le titre est obligatoire'); return; }
    try {
      await opportuniteService.create(form);
      toast.success('Opportunité créée');
      setShowCreate(false);
      setForm({ titre: '', montantEstime: 0, etape: EtapeOpportunite.PROSPECTION });
      load();
    } catch {
      toast.error('Création impossible');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Pipeline commercial</h1>
          <p className="page-subtitle">Opportunités par étape, prévision pondérée</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary">
          <PlusIcon className="h-4 w-4 mr-1" /> Nouvelle opportunité
        </button>
      </div>

      {pipeline && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="card">
            <p className="text-sm text-gray-500">Opportunités ouvertes</p>
            <p className="text-2xl font-semibold">{pipeline.nbOuvertes}</p>
          </div>
          <div className="card">
            <p className="text-sm text-gray-500">Total estimé (ouvert)</p>
            <MoneyDisplay amount={pipeline.totalOuvertEstime} className="text-2xl font-semibold" />
          </div>
          <div className="card">
            <p className="text-sm text-gray-500">Prévision pondérée</p>
            <MoneyDisplay amount={pipeline.previsionPonderee} className="text-2xl font-semibold text-green-700" />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {pipeline?.colonnes.map((col) => (
          <div key={col.etape} className={`bg-gray-50 rounded-lg border-t-4 ${COLUMN_STYLE[col.etape]} p-2`}>
            <div className="px-1 pb-2">
              <p className="text-sm font-semibold">{ETAPE_OPPORTUNITE_LABELS[col.etape]}</p>
              <p className="text-xs text-gray-500">
                {col.nombre} · <MoneyDisplay amount={col.totalEstime} size="sm" />
              </p>
            </div>
            <div className="space-y-2">
              {col.opportunites.map((o) => (
                <div key={o.id} className="bg-white rounded-md shadow-sm p-2 text-sm">
                  <p className="font-medium truncate" title={o.titre}>{o.titre}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {o.clientRaisonSociale || o.prospectNom || '—'}
                  </p>
                  <div className="flex items-center justify-between mt-1">
                    <MoneyDisplay amount={o.montantEstime} size="sm" />
                    <span className="text-xs text-gray-400">{o.probabilite}%</span>
                  </div>
                  {!col.etape.toString().match(/GAGNEE|PERDUE/) && (
                    <div className="flex gap-1 mt-2">
                      {NEXT[o.etape] && (
                        <button onClick={() => advance(o)} className="btn-outline px-2 py-1 text-xs flex-1"
                                title={`Avancer vers ${ETAPE_OPPORTUNITE_LABELS[NEXT[o.etape]!]}`}>
                          <ArrowRightIcon className="h-3 w-3 inline" />
                        </button>
                      )}
                      <button onClick={() => lose(o)} className="btn-danger px-2 py-1 text-xs"
                              title="Marquer perdue">
                        <XMarkIcon className="h-3 w-3 inline" />
                      </button>
                    </div>
                  )}
                  {o.motifPerte && <p className="text-xs text-red-500 mt-1">Perdue : {o.motifPerte}</p>}
                </div>
              ))}
              {col.opportunites.length === 0 && (
                <p className="text-xs text-gray-300 px-1 py-3 text-center">—</p>
              )}
            </div>
          </div>
        ))}
      </div>

      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Nouvelle opportunité">
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
            <input className="input-field" value={form.titre ?? ''}
                   onChange={(e) => setForm({ ...form, titre: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
            <select className="input-field" value={form.clientId ?? ''}
                    onChange={(e) => setForm({ ...form, clientId: e.target.value ? Number(e.target.value) : null })}>
              <option value="">— Prospect (sans client) —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.raisonSociale}</option>)}
            </select>
          </div>
          {!form.clientId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom du prospect</label>
              <input className="input-field" value={form.prospectNom ?? ''}
                     onChange={(e) => setForm({ ...form, prospectNom: e.target.value })} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Montant estimé</label>
              <input type="number" className="input-field" value={form.montantEstime ?? 0}
                     onChange={(e) => setForm({ ...form, montantEstime: Number(e.target.value) })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Étape</label>
              <select className="input-field" value={form.etape}
                      onChange={(e) => setForm({ ...form, etape: e.target.value as EtapeOpportunite })}>
                {Object.values(EtapeOpportunite).map((et) => (
                  <option key={et} value={et}>{ETAPE_OPPORTUNITE_LABELS[et]}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Clôture prévue</label>
            <input type="date" className="input-field" value={form.dateCloturePrev ?? ''}
                   onChange={(e) => setForm({ ...form, dateCloturePrev: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setShowCreate(false)} className="btn-outline">Annuler</button>
            <button onClick={submitCreate} className="btn-primary">Créer</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default CrmPipeline;
